import { DatePicker } from '@progress/kendo-react-dateinputs';
import { GridCellProps } from '@progress/kendo-react-grid';
import { addDays, format, isBefore, subDays } from 'date-fns';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import GridCellBase from 'src/components/GridCellBase';
import useViewCalendarYear from 'src/hooks/useViewCalendarYear';
import { removeTime } from 'src/utils/formatTime';
import { getBgColor } from 'src/utils/icons';

const EndDate = (props: GridCellProps) => {
  const { isPeriodEditMode, selectedFYPeriod, latestFYPeriod } = useViewCalendarYear();
  const { dataItem, dataIndex, field = '', onChange } = props;
  const { startDate, errors } = dataItem;
  const value = dataItem[field];
  const formatValue = value ? format(value, 'MM/dd/yyyy') : '';
  const currentDate = removeTime(new Date());
  const hasPassed = isBefore(value, currentDate);
  const isLatestYear = latestFYPeriod?.fiscalYear === selectedFYPeriod?.fiscalYear;
  const isLastPeriod =
    selectedFYPeriod?.periods[selectedFYPeriod.periods.length - 1].rowId === dataItem.rowId;

  const inputRef = useRef<HTMLInputElement | null>(null);

  const min =
    selectedFYPeriod.isCurrYear && startDate && isBefore(startDate, currentDate)
      ? addDays(currentDate, 1)
      : selectedFYPeriod.isCurrYear && !startDate
      ? addDays(currentDate, 1)
      : startDate
      ? addDays(startDate, 1)
      : addDays(selectedFYPeriod.yearStartDateObj, 1);

  const max = !(isLatestYear && isLastPeriod)
    ? subDays(selectedFYPeriod.yearEndDateObj, 2)
    : undefined;
  const valid = useMemo(() => !errors.endDate.length, [errors.endDate.length]);
  const isDisable = useMemo(
    () => (isLastPeriod ? (isLatestYear ? false : true) : !valid ? false : hasPassed),
    [hasPassed, isLastPeriod, isLatestYear, valid]
  );

  const preventDefault = useCallback((e: any) => {
    e.preventDefault();
  }, []);

  const handleChange = useCallback(
    (e: any) => {
      onChange?.({
        syntheticEvent: e.syntheticEvent,
        dataIndex,
        dataItem,
        field,
        value: e.value,
      });
    },
    [dataIndex, dataItem, field, onChange]
  );

  const handleBlurChange = useCallback(
    (e: any) => {
      const date: string = e.target.value;
      e.preventDefault();
      if (date) {
        if (!date.includes('year') && !date.includes('month') && !date.includes('day')) {
          const momentDate = moment(date, 'MM/DD/YYYY');
          if (momentDate.get('year') < 10) {
            handleChange({
              syntheticEvent: e.syntheticEvent,
              value: new Date(momentDate.add(2000, 'years').format()),
            });
          } else {
            handleChange({
              syntheticEvent: e.syntheticEvent,
              value: new Date(momentDate.format()),
            });
          }
        } else {
          handleChange({
            syntheticEvent: e.syntheticEvent,
            value: null,
          });
        }
      }
    },
    [handleChange]
  );

  useEffect(() => {
    if (inputRef?.current && dataItem?.isNewPeriod) {
      inputRef?.current?.focus();
    }
  }, [dataItem?.isNewPeriod]);

  return (
    <GridCellBase
      {...props}
      className={`${getBgColor(errors.endDate, dataItem.warnings.endDate)} !text-center`}
      title={formatValue}
    >
      {isPeriodEditMode ? (
        <span onDragStart={preventDefault} onDragOver={preventDefault}>
          <DatePicker
            disabled={isDisable}
            value={value}
            onChange={handleChange}
            onBlur={handleBlurChange}
            valid={valid}
            min={min}
            max={max}
            size="small"
            ref={inputRef}
          />
        </span>
      ) : (
        formatValue
      )}
    </GridCellBase>
  );
};

export default EndDate;
